import React, { useState } from "react";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import { DateFormat } from "../../../helpers/fixcodes";
import { activityTone, genderText } from "./crewActivityRules";
import ActivityActions, { actionsWidth, mostActions } from "./ActivityActions";

const DT = {
  dataType: "datetime",
  format: DateFormat.DisplayDateTime24NorthFormat,
};
const SEARCHABLE = { allowSearch: true, headerFilter: { allowSearch: true } };

/** Activity code + location (non fly; location first for a ground duty pick-up) or the flight. */
export function ActivityCell({ row }) {
  if (row.activityLocationCode) {
    const code = (
      <span>
        <i className="fas fa-map-marker-alt" aria-hidden="true" />{" "}
        {row.activityCode ?? ""}
      </span>
    );
    const location = (
      <span className="text-muted">
        <i className="fas fa-building" aria-hidden="true" />{" "}
        {row.activityLocationCode}
      </span>
    );
    return row.isGroundDutyPickup ? (
      <span className="dsp-activity">
        {location}
        {code}
      </span>
    ) : (
      <span className="dsp-activity">
        {code}
        {location}
      </span>
    );
  }
  return (
    <span className="dsp-activity">
      <span>
        <i className="fas fa-plane-departure" aria-hidden="true" />{" "}
        {row.activityDisplay}
      </span>
    </span>
  );
}

/**
 * Desktop grid of the crew activities: same columns as before, the row tone as a thin accent (hotel can be changed /
 * non fly / fly, same colours, CSS classes), the actions in the fixed first column, search and Excel export.
 */
export default function CrewActivityGrid({ rows, gridRef, actions }) {
  // The actions column fits the rows on screen: two buttons when a visible row can change its hotel, else one. Checked
  // each time the grid is drawn (search, tile, filter, sort, page) and kept in state, so the column's width prop is the
  // only place it is set (a width set straight on the grid was put back by the next render).
  const [actionsColumnWidth, setActionsColumnWidth] = useState(() => actionsWidth(1));
  const fitActions = (e) => {
    const visible = e.component
      .getVisibleRows()
      .filter((r) => r.rowType === "data")
      .map((r) => r.data);
    const width = actionsWidth(mostActions(visible, actions));
    setActionsColumnWidth((current) => (current === width ? current : width));
  };
  return (
    <DataGrid
      ref={gridRef}
      dataSource={rows}
      className="dsp-grid cra-grid"
      columnAutoWidth
      showColumnLines={false}
      showRowLines
      showBorders
      allowColumnResizing
      allowColumnReordering
      repaintChangesOnly
      onContentReady={fitActions}
      onRowPrepared={(e) => {
        if (e.rowType === "data")
          e.rowElement.classList.add(
            "dsp-row",
            `is-cra-${activityTone(e.data)}`,
          );
      }}
    >
      <Column
        name="actions"
        caption=""
        fixed
        width={actionsColumnWidth}
        alignment="center"
        allowSorting={false}
        allowFiltering={false}
        allowExporting={false}
        cssClass="dsp-act-cell"
        cellRender={({ data }) => <ActivityActions row={data} {...actions} />}
      />
      <Column dataField="activityType" caption="Duty type" {...SEARCHABLE} />
      <Column
        dataField="activityDisplay"
        caption="Activity / Flight"
        width={"190"}
        {...SEARCHABLE}
        cellRender={({ data }) => <ActivityCell row={data} />}
      />
      <Column dataField="employeeNumber" caption="Staff ID #" {...SEARCHABLE} />
      <Column dataField="fullName" caption="Staff name" {...SEARCHABLE} />
      <Column
        dataField="airlineCrewType"
        caption="Staff rank"
        {...SEARCHABLE}
      />
      <Column
        name="gender"
        caption="Gender"
        calculateCellValue={genderText}
        {...SEARCHABLE}
        allowFiltering
        allowHeaderFiltering
        allowSorting
      />
      <Column
        dataField="crewBaseStation"
        caption="Staff base"
        {...SEARCHABLE}
      />
      <Column
        dataField="airportCode"
        caption="Pick-up / Drop airport"
        {...SEARCHABLE}
      />
      <Column
        dataField="nextDutyAirport"
        caption="Next duty start airport"
        {...SEARCHABLE}
      />
      <Column
        dataField="transportationCompany"
        caption="Transporter"
        {...SEARCHABLE}
      />
      <Column
        dataField="pickupLocation"
        caption="Pickup"
        width={210}
        {...SEARCHABLE}
      />
      <Column
        dataField="dropOffLocation"
        caption="Dropoff"
        width={210}
        {...SEARCHABLE}
      />
      <Column
        dataField="flightTime"
        caption="Flight time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column
        dataField="pickupTime"
        caption="Pickup time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column
        dataField="dutyReportingTime"
        caption="Reporting time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column
        dataField="deBriefingTime"
        caption="Debriefing time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column
        dataField="dropoffTime"
        caption="Dropoff time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column
        dataField="activityStartTime"
        caption="Activity start time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column
        dataField="activityEndTime"
        caption="Activity end time"
        {...DT}
        {...SEARCHABLE}
      />
      <Column dataField="flightNumber" caption="Flight #" {...SEARCHABLE} />
      <Column
        dataField="flightArrivalTerminal"
        caption="Arrival terminal"
        {...SEARCHABLE}
      />
      <Column
        dataField="flightDepartureTerminal"
        caption="Departure terminal"
        {...SEARCHABLE}
      />
      <Column
        dataField="activityLocationCode"
        caption="Activity location code"
        {...SEARCHABLE}
      />
      <Column
        dataField="transactionId"
        caption="Transaction #"
        {...SEARCHABLE}
      />
      <Column
        dataField="tripId"
        caption="Tracking #"
        dataType="string"
        {...SEARCHABLE}
        cellRender={({ data }) => data?.tripId || ""}
      />
      <Column dataField="tripTypeName" caption="Trip type" {...SEARCHABLE} />
      <HeaderFilter visible />
      <Paging defaultPageSize={20} />
      <Pager
        displayMode="compact"
        showPageSizeSelector
        allowedPageSizes={[10, 20, 50, 100]}
        showInfo
        visible
      />
      <SearchPanel visible width={240} placeholder="Search activities..." />
      <Export enabled allowExportSelectedData={false} fileName="CrewActivity" />
    </DataGrid>
  );
}
